'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import type { Resolver } from 'react-hook-form-mui';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { zodResolver } from '@hookform/resolvers/zod';
import { formControlClasses, styled } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { AnimatePresence, motion } from 'framer-motion';
import { submitForm } from '@/app/actions/form';
import { CmsHtmlContent } from '@/components/CmsHtmlContent';
import { EditableFormStep } from '@/components/UserDefinedForm/Fields/EditableFormStep';
import { createZodSchemaForFields } from '@/components/UserDefinedForm/lib/createZodSchemaForFields';
import { extractDefaultValues } from '@/components/UserDefinedForm/lib/extractDefaultValues';
import { groupEditableFields } from '@/components/UserDefinedForm/lib/groupEditableFields';
import { ZoomSpinner } from '@/components/ZoomSpinner';
import type { CmsFormSubmissionData } from '@/services/cms/CmsFormSubmissionData';
import type { CmsUserDefinedFormPageData } from '@/services/cms/pages/CmsUserDefinedFormPageData';
import type { CmsEditableField } from '@/services/cms/userDefinedForm/CmsEditableField';
export interface UserDefinedFormProps {
  onCompleteMessage: CmsUserDefinedFormPageData['onCompleteMessage'];
  showClearButton: CmsUserDefinedFormPageData['showClearButton'];
  enableLiveValidation: CmsUserDefinedFormPageData['enableLiveValidation'];
  submitButtonText: CmsUserDefinedFormPageData['submitButtonText'];
  clearButtonText: CmsUserDefinedFormPageData['clearButtonText'];
  fields: CmsEditableField[];
  formClassName: string;
  formId: number;
  siteId?: number;
  locale?: string;
}
export function UserDefinedForm(t0) {
  const $ = _c(40);
  const {
    onCompleteMessage,
    showClearButton,
    fields,
    submitButtonText,
    clearButtonText,
    formClassName,
    formId,
    siteId,
    locale,
    enableLiveValidation
  } = t0;
  const [message, setMessage] = React.useState(null);
  const t1 = siteId || 0;
  const t2 = locale || null;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Date().getTime();
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== fields) {
    t4 = extractDefaultValues(fields);
    $[1] = fields;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== formClassName || $[4] !== formId || $[5] !== t1 || $[6] !== t2 || $[7] !== t4) {
    t5 = {
      siteId: t1,
      locale: t2,
      formId,
      formClassName,
      date: t3,
      ...t4
    };
    $[3] = formClassName;
    $[4] = formId;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const defaultFormValues = t5 as CmsFormSubmissionData;
  let t6;
  let t7;
  let t8;
  if ($[9] !== defaultFormValues || $[10] !== enableLiveValidation || $[11] !== fields) {
    const schema = createZodSchemaForFields(fields);
    t8 = useForm;
    t6 = defaultFormValues;
    t7 = enableLiveValidation ? zodResolver(schema) as unknown as Resolver<CmsFormSubmissionData> : undefined;
    $[9] = defaultFormValues;
    $[10] = enableLiveValidation;
    $[11] = fields;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t7) {
    t9 = {
      defaultValues: t6,
      resolver: t7
    };
    $[15] = t6;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const form = t8(t9);
  const pending = form.formState.isSubmitting || form.formState.isValidating;
  let T0;
  let T1;
  let t10;
  let t11;
  if ($[18] !== clearButtonText || $[19] !== fields || $[20] !== form || $[21] !== message || $[22] !== onCompleteMessage || $[23] !== pending || $[24] !== showClearButton || $[25] !== submitButtonText) {
    const fieldSteps = groupEditableFields(fields);
    let t12;
    if ($[30] !== form || $[31] !== onCompleteMessage) {
      t12 = async data => {
        const response = await submitForm(null, data);
        if (!response.success) {
          const fieldErrors = response.errors || [];
          for (const error of fieldErrors) {
            form.setError(error.fieldName, {
              type: "manual",
              message: error.message
            });
          }
          if (!fieldErrors.length) {
            form.setError("root", {
              type: "manual",
              message: response.message || "An error occurred while submitting the form."
            });
          }
        }
        setMessage(response.message || onCompleteMessage);
      };
      $[30] = form;
      $[31] = onCompleteMessage;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    const handleSubmit = t12;
    T1 = UserDefinedFormRoot;
    T0 = AnimatePresence;
    t10 = "wait";
    t11 = form.formState.isSubmitSuccessful ? <motion.div key="form-complete" initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -24
    }} transition={{
      duration: 0.4,
      ease: "easeOut"
    }}><CmsHtmlContent html={message} /></motion.div> : <motion.div key="form-fields" animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -24
    }} transition={{
      duration: 0.4,
      ease: "easeOut"
    }}><FormContainer formContext={form} onSuccess={handleSubmit}>{fieldSteps.map(_temp)}<Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 2,
          flexDirection: "column",
          gap: 4
        }}>{showClearButton ? <Button type="reset" disabled={pending}>{clearButtonText || "Clear"}</Button> : null}<Button type="submit" variant="contained" color="secondary" disabled={pending}>{submitButtonText || "Submit"}<ZoomSpinner show={pending} /></Button>{form.formState.errors.root?.message ? <Alert severity="error" sx={{
            marginLeft: 2
          }} hidden={!form.formState.errors.root?.message}><AlertTitle>Failed to submit form</AlertTitle>{form.formState.errors.root?.message}</Alert> : null}</Box></FormContainer></motion.div>;
    $[18] = clearButtonText;
    $[19] = fields;
    $[20] = form;
    $[21] = message;
    $[22] = onCompleteMessage;
    $[23] = pending;
    $[24] = showClearButton;
    $[25] = submitButtonText;
    $[26] = T0;
    $[27] = T1;
    $[28] = t10;
    $[29] = t11;
  } else {
    T0 = $[26];
    T1 = $[27];
    t10 = $[28];
    t11 = $[29];
  }
  let t12;
  if ($[33] !== T0 || $[34] !== t10 || $[35] !== t11) {
    t12 = <T0 mode={t10}>{t11}</T0>;
    $[33] = T0;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== T1 || $[38] !== t12) {
    t13 = <T1>{t12}</T1>;
    $[37] = T1;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
}
function _temp(field) {
  return <EditableFormStep {...field} key={field.name} />;
}
const UserDefinedFormRoot = styled('div')(({
  theme
}) => ({
  [`& .${formControlClasses.root}`]: {
    width: '100%',
    marginBottom: theme.spacing(2)
  }
}));